import {
  applyRequestChanges,
  baselineEntityOf,
  cardVersions,
  payloadEntityOf,
  pendingSlotsOf,
  requestDecision,
  requestOperation,
  slotOfRequest,
  useModerationCard,
  useModerationRequest,
  type ChangeRequest,
  type ModerationCard,
  type ModerationDecision,
  type ModerationEntity,
  type ModerationEntityType,
  type ModerationOperation,
  type ModerationVersion,
  type PendingSlotVersion,
} from '@/entities/moderation';

/** Where the review card stands: `idle` when the request has no entity yet. */
export type CardStatus = 'idle' | 'loading' | 'error' | 'ready';

export interface ModerationReview {
  /** The freshest copy of the request (re-read on open, seeded by the row). */
  request: ChangeRequest;
  operation: ModerationOperation;
  decision: ModerationDecision;
  card: ModerationCard | undefined;
  cardStatus: CardStatus;
  refetchCard: () => void;
  /** Every present card version (`prod` first) for the per-branch grids. */
  versions: ModerationVersion[];
  /** The live row: `prod`, else the request's `baseline`. `null` for a create. */
  live: ModerationEntity | null;
  /** The card's pending slots (`manual` then `sync`). */
  pendingSlots: PendingSlotVersion[];
  /** The slot holding THIS request, when the card still carries it. */
  ownSlot: PendingSlotVersion | undefined;
  /**
   * A pending edit the loaded card does not carry in either slot — the
   * compare then falls back to the request's own payload.
   */
  missingFromCard: boolean;
  /** This request's proposal as a full entity (slot entity, else live + payload). */
  proposed: ModerationEntity;
  /**
   * What the single-column views render: the proposed entity for a create,
   * the entity being removed for a delete, the live row for a decided update.
   */
  snapshot: ModerationEntity;
}

/**
 * Resolves everything the review modal branches on. Operation and decision
 * come from the request itself, so the right modal renders immediately —
 * the card only fills its content (sections show skeletons meanwhile).
 */
export function useModerationReview(
  entityType: ModerationEntityType,
  seed: ChangeRequest,
): ModerationReview {
  const { data: request = seed } = useModerationRequest(seed);
  const entityId = request.entity_id?.trim() ?? '';
  const cardQuery = useModerationCard(entityType, entityId);

  const card = cardQuery.data;
  const cardStatus: CardStatus =
    entityId === ''
      ? 'idle'
      : cardQuery.isPending
        ? 'loading'
        : cardQuery.isError
          ? 'error'
          : 'ready';

  const operation = requestOperation(request);
  const decision = requestDecision(request);
  const pendingSlots = pendingSlotsOf(card);
  const ownSlot = slotOfRequest(card, request);

  const baseline = baselineEntityOf(request);
  const payload = payloadEntityOf(request);
  const prodEntity = card?.prod?.entity ?? null;
  // The request's `baseline` is the authoritative "before": the card's `prod`
  // entity omits several fields (cook time, clear_*, availability window), so
  // it only fills what the baseline lacks.
  const live = prodEntity || baseline ? { ...prodEntity, ...baseline } : null;

  // The slot entity is the live row with this request's patch applied — a
  // full entity. Without it (no card yet, no entity id, or the card moved
  // on) the payload is laid over the live row: payloads are patches, and a
  // publication's is often just `{lifecycle_status}`.
  const proposed: ModerationEntity = ownSlot
    ? applyRequestChanges(ownSlot.version.entity ?? {}, request)
    : { ...baseline, ...prodEntity, ...payload };

  const snapshot: ModerationEntity =
    operation === 'create'
      ? proposed
      : operation === 'delete'
        ? { ...baseline, ...prodEntity }
        : (prodEntity ?? baseline ?? proposed);

  return {
    request,
    operation,
    decision,
    card,
    cardStatus,
    refetchCard: () => void cardQuery.refetch(),
    versions: cardVersions(card),
    live,
    pendingSlots,
    ownSlot,
    missingFromCard:
      operation === 'update' &&
      decision === 'pending' &&
      cardStatus === 'ready' &&
      !ownSlot,
    proposed,
    snapshot,
  };
}
